/**
 * VideoUploadButton Component
 * Button that triggers file input for video upload
 */

import { useRef } from 'react';
import { Button } from '../../../../../components/Button';
import { AddIcon } from '../../../../../assets/AddIcon';
import { validateVideoFile } from '../../../../../utils/video-validation';

interface VideoUploadButtonProps {
  onUpload: (file: File) => void;
  onValidationError?: (error: string) => void;
  disabled: boolean;
}

export function VideoUploadButton({ onUpload, onValidationError, disabled }: VideoUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = await validateVideoFile(file);
    if (!validation.valid) {
      if (onValidationError) {
        onValidationError(validation.error || 'Invalid video file');
      }
      // Reset input
      event.target.value = '';
      return;
    }

    onUpload(file);

    // Reset input so same file can be uploaded again if needed
    event.target.value = '';
  };

  return (
    <>
      <Button
        variant="primary"
        onClick={handleButtonClick}
        disabled={disabled}
        aria-label="Upload video"
      >
        <AddIcon />
        Add Video
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </>
  );
}
